import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createProjectGroupsTasks,
  createProjectGroupsTasksMemos,
  deleteProjectGroupsTasksAssignees,
  deleteProjectGroupsTasksMemos,
  inviteProjectGroupsTasksAssignees,
  readProjectGroupsOneTask,
  readProjectGroupsTasks,
  updateProjectGroupsTasks,
  updateProjectGroupsTasksMemos,
} from "@/services/api/task";
import {
  CreateProjectGroupsTasksMemosRequest,
  CreateProjectGroupsTasksRequest,
  InviteProjectGroupsTasksAssigneesRequest,
  RemoveProjectGroupsTasksAssigneesRequest,
  UpdateProjectGroupsTasksMemosRequest,
  UpdateProjectGroupsTasksRequest,
} from "@/types/task";
import { QUERY_KEYS } from "../queryKeys";

// 프로젝트 그룹 작업 생성
export const useCreateProjectGroupsTasks = (group_id: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateProjectGroupsTasksRequest) =>
      createProjectGroupsTasks({ group_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
    },
  });
};

// 단일 프로젝트 작업 목록 조회
export const useReadProjectGroupsTasks = (group_id: string) => {
  return useQuery({
    queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
    queryFn: () => readProjectGroupsTasks({ group_id }),
  });
};

// 프로젝트 그룹 작업 정보 조회
export const useReadProjectGroupsOneTask = (
  group_id: string,
  task_id: string
) => {
  return useQuery({
    queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
    queryFn: () => readProjectGroupsOneTask({ group_id, task_id }),
    enabled: !!group_id && !!task_id,
  });
};

// 프로젝트 그룹 작업 정보 변경
export const useUpdateProjectGroupsTasks = ({
  group_id,
  task_id,
}: {
  group_id: string;
  task_id: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProjectGroupsTasksRequest) =>
      updateProjectGroupsTasks({ group_id, task_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TASKS.ALL() });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.LIST(),
      });
    },
  });
};

// 프로젝트 그룹 작업 담당자 초대
export const useInviteProjectGroupsTasksAssignees = ({
  group_id,
  task_id,
}: {
  group_id: string;
  task_id: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: InviteProjectGroupsTasksAssigneesRequest) =>
      inviteProjectGroupsTasksAssignees({ group_id, task_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TASKS.ALL() });
    },
  });
};

// 프로젝트 그룹 작업 담당자 제거
export const useRemoveProjectGroupsTasksAssignees = ({
  group_id,
  task_id,
}: {
  group_id: string;
  task_id: string;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: RemoveProjectGroupsTasksAssigneesRequest) =>
      deleteProjectGroupsTasksAssignees({ group_id, task_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
      });
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.TASKS.ALL() });
    },
  });
};

export const useCreateProjectGroupsTasksMemos = ({
  group_id,
  task_id,
}: {
  group_id: string;
  task_id: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateProjectGroupsTasksMemosRequest) =>
      createProjectGroupsTasksMemos({ group_id, task_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
      });
    },
  });
};

export const useUpdateProjectGroupsTasksMemos = ({
  group_id,
  task_id,
  memo_id,
}: {
  group_id: string;
  task_id: string;
  memo_id: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: UpdateProjectGroupsTasksMemosRequest) =>
      updateProjectGroupsTasksMemos({ group_id, task_id, memo_id, data }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
      });
    },
  });
};

export const useDeleteProjectGroupsTasksMemos = ({
  group_id,
  task_id,
  memo_id,
}: {
  group_id: string;
  task_id: string;
  memo_id: string;
}) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      deleteProjectGroupsTasksMemos({ group_id, task_id, memo_id }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.PROJECT_GROUPS.TASKS.LIST(group_id),
      });
      queryClient.invalidateQueries({
        queryKey: QUERY_KEYS.TASKS.DETAIL(task_id),
      });
    },
  });
};
